// useStagingTray.ts
import { useCallback, useEffect, useRef, useState } from "react"
import { isAxiosError } from "axios"
import { Editor as TiptapEditor, getHTMLFromFragment } from "@tiptap/react"
import { Fragment, Node as ProseMirrorNode } from "@tiptap/pm/model"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  mergeTemplateTray,
  validateQuestionnaireText,
} from "api/Engine/templates"
import { failureOf } from "utils/errMsgOf"
import { htmlToPlainText, stripCommentSpans } from "utils/editorUtils"
import { insertTextAtCursor } from "utils/insertTextAtCursor"
import { withSurveyFrame } from "utils/surveyFrame"
import { CheckQuestionnaireResult } from "types/questionnaire.types"
import {
  StagedBlock,
  TrayMergeResult,
  TrayNameClash,
} from "types/template.types"

/** How long the check waits after the last tray change. */
export const CHECK_DEBOUNCE_MS = 400

export interface TrayCheck {
  /** "idle" with an empty tray; "checking" from a change until the answer */
  state: "idle" | "checking" | "ok" | "errors"
  /** Errors per block (tray index, 0-based) */
  byBlock: Record<number, string[]>
  /** Errors not tied to one block (a failed request, the frame, the joined EXCEPTIONS) */
  general: string[]
  /** Warnings and unfinished notes in the inserted text; they do not block Insert */
  warnings: string[]
}

export interface StagingTray {
  blocks: StagedBlock[]
  /** Appends a block */
  add: (block: StagedBlock) => void
  /** Replaces the block with the same id (or appends it) */
  update: (block: StagedBlock) => void
  remove: (id: string) => void
  /** Moves the block at `from` to index `to` */
  move: (from: number, to: number) => void
  clear: () => void
  check: TrayCheck
  /** Inserts the tray at the cursor (one edit, one undo) and empties it */
  insert: () => Promise<void>
}

const IDLE: TrayCheck = { state: "idle", byBlock: {}, general: [], warnings: [] }
const CHECKING: TrayCheck = { ...IDLE, state: "checking" }

/**
 * Where the tray goes in the editor: after the cursor's line (at the end of
 * that paragraph, so a mid-line cursor does not split it), or in place of
 * the cursor's line when it is empty (an empty editor gets it at the start).
 * The editor is read one paragraph per line, so the line numbers of the
 * text match the paragraphs exactly.
 */
export interface InsertPoint {
  /** The editor's lines before the inserted text */
  before: string[]
  /** The editor's lines after the inserted text */
  after: string[]
  /** The document position handed to `insertTextAtCursor` */
  pos: number
}

/** One editor paragraph as a questionnaire line (as Save writes it). */
const lineOf = (editor: TiptapEditor, node: ProseMirrorNode) =>
  htmlToPlainText(
    stripCommentSpans(getHTMLFromFragment(Fragment.from(node), editor.schema))
  )

/**
 * Splits the editor around the insert point (see `InsertPoint`).
 *
 * @param editor - The questionnaire editor
 */
export const insertPoint = (editor: TiptapEditor): InsertPoint => {
  const { doc, selection } = editor.state
  const lines: string[] = []
  const ends: number[] = []
  const empty: boolean[] = []
  doc.forEach((node, offset) => {
    lines.push(lineOf(editor, node))
    // The end of a paragraph's content; after a non-text block.
    ends.push(node.isTextblock ? offset + node.nodeSize - 1 : offset + node.nodeSize)
    empty.push(node.isTextblock && node.content.size === 0)
  })
  if (lines.length === 0) return { before: [], after: [], pos: 0 }

  const index = Math.min(selection.$from.index(0), lines.length - 1)
  // An empty line is replaced by the insert; otherwise it goes after it.
  const cut = empty[index] ? index : index + 1
  return {
    before: lines.slice(0, cut),
    after: lines.slice(index + 1),
    pos: ends[index],
  }
}

/**
 * Maps the engine's notes on the would-be questionnaire to the tray. Only
 * notes inside the inserted text count: errors in a block's line range go to
 * that block, others in the inserted text are general; warnings and
 * unfinished notes are listed.
 *
 * @param result - The engine check of the would-be questionnaire
 * @param merged - The tray merge (block line ranges, 1-based in its text)
 * @param firstTrayLine - The line of the would-be text holding the tray's line 1
 * @param inserted - The first and last line of the inserted text (frame included)
 */
export const mapNotes = (
  result: CheckQuestionnaireResult,
  merged: TrayMergeResult,
  firstTrayLine: number,
  inserted: [number, number]
): TrayCheck => {
  const byBlock: Record<number, string[]> = {}
  const general: string[] = []
  const inside = (line: number) => line >= inserted[0] && line <= inserted[1]
  const shift = firstTrayLine - 1

  for (const error of result.notifications?.errors ?? []) {
    const line = error["line nbr"]
    if (!inside(line)) continue
    const index = merged.blocks.findIndex(
      (b) => line >= b.firstLine + shift && line <= b.lastLine + shift
    )
    if (index < 0) general.push(error.message)
    else (byBlock[index] ??= []).push(error.message)
  }
  const warnings = [
    ...(result.notifications?.warnings ?? []),
    ...(result.notifications?.unfinished ?? []),
  ]
    .filter((w) => inside(w["line nbr"]))
    .map((w) => w.message)

  const failed = general.length > 0 || Object.keys(byBlock).length > 0
  return { state: failed ? "errors" : "ok", byBlock, general, warnings }
}

/**
 * The would-be questionnaire: the editor with the framed tray text at the
 * insert point. The tray text loses its trailing line ends (no empty
 * paragraph after it) and gets one blank line before it, unless it goes at
 * the start of the document. Check and insert both use this text, so the
 * line mapping stays exact.
 */
const wouldBe = (
  editor: TiptapEditor,
  trayText: string,
  projectName: string
) => {
  const point = insertPoint(editor)
  const editorText = [...point.before, ...point.after].join("\n")
  const tray = trayText.replace(/(\r?\n)+$/, "")
  const body = withSurveyFrame(editorText, tray, projectName)
  const framed = point.before.length > 0 ? "\n" + body : body
  const insertedLines = framed.split(/\r?\n/)
  const frameLines = insertedLines.length - tray.split(/\r?\n/).length
  return {
    point,
    framed,
    text: [...point.before, ...insertedLines, ...point.after].join("\n"),
    firstTrayLine: point.before.length + frameLines + 1,
    inserted: [
      point.before.length + 1,
      point.before.length + insertedLines.length,
    ] as [number, number],
  }
}

/**
 * The staging tray of the Templates dialog: the staged blocks, the check of
 * the would-be questionnaire, and Insert.
 *
 * The check runs 400 ms after the last tray change (only the latest answer
 * counts): the whole tray is merged by the server (a name clash, 422, maps
 * to its blocks), then the editor text with the merged tray at the insert
 * point is parsed, and the parser's errors are mapped to the blocks.
 *
 * @param projectId - The project the templates are merged for
 * @param owner - The project owner (hint_owner of the template routes)
 * @param editor - The questionnaire editor
 * @param projectName - For the SURVEY line when the questionnaire has none
 * @returns The tray
 */
export const useStagingTray = (
  projectId: string,
  owner: string,
  editor: TiptapEditor | null,
  projectName: string
): StagingTray => {
  const getAxios = useAuthedAxios()
  const [blocks, setBlocks] = useState<StagedBlock[]>([])
  const [check, setCheck] = useState<TrayCheck>(IDLE)
  const latest = useRef(0)
  // The merge of the blocks as they were last checked.
  const merged = useRef<{ key: string; result: TrayMergeResult } | null>(null)

  const key = JSON.stringify(blocks)

  useEffect(() => {
    const seq = ++latest.current
    if (blocks.length === 0) {
      setCheck(IDLE)
      return
    }
    setCheck(CHECKING)
    const timer = setTimeout(() => {
      void (async () => {
        const live = () => seq === latest.current
        let result: TrayMergeResult
        try {
          result = await mergeTemplateTray(await getAxios(), {
            id: projectId,
            owner,
            blocks,
          })
        } catch (e) {
          if (!live()) return
          const clash =
            isAxiosError(e) && e.response?.status === 422
              ? (e.response.data as { blocks?: TrayNameClash } | undefined)
                  ?.blocks
              : undefined
          if (clash) {
            const byBlock: Record<number, string[]> = {}
            for (const c of clash) (byBlock[c.index] ??= []).push(...c.errors)
            setCheck({ state: "errors", byBlock, general: [], warnings: [] })
          } else {
            setCheck({ ...IDLE, state: "errors", general: [failureOf(e)] })
          }
          return
        }
        if (!live()) return
        merged.current = { key, result }
        if (!editor || editor.isDestroyed) {
          setCheck({ ...IDLE, state: "ok" })
          return
        }
        const plan = wouldBe(editor, result.text, projectName)
        try {
          const answer = await validateQuestionnaireText(
            await getAxios(),
            plan.text
          )
          if (live())
            setCheck(mapNotes(answer, result, plan.firstTrayLine, plan.inserted))
        } catch (e) {
          if (live())
            setCheck({ ...IDLE, state: "errors", general: [failureOf(e)] })
        }
      })()
    }, CHECK_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [blocks, key, projectId, owner, editor, projectName, getAxios])

  const add = useCallback(
    (block: StagedBlock) => setBlocks((prev) => [...prev, block]),
    []
  )
  const update = useCallback(
    (block: StagedBlock) =>
      setBlocks((prev) =>
        prev.some((b) => b.id === block.id)
          ? prev.map((b) => (b.id === block.id ? block : b))
          : [...prev, block]
      ),
    []
  )
  const remove = useCallback(
    (id: string) => setBlocks((prev) => prev.filter((b) => b.id !== id)),
    []
  )
  const move = useCallback(
    (from: number, to: number) =>
      setBlocks((prev) => {
        if (to < 0 || to >= prev.length || from === to) return prev
        const next = [...prev]
        const [block] = next.splice(from, 1)
        next.splice(to, 0, block)
        return next
      }),
    []
  )
  const clear = useCallback(() => setBlocks([]), [])

  const insert = useCallback(async () => {
    if (!editor || editor.isDestroyed || blocks.length === 0) return
    const trayText =
      merged.current?.key === key
        ? merged.current.result.text
        : (
            await mergeTemplateTray(await getAxios(), {
              id: projectId,
              owner,
              blocks,
            })
          )
            .text
    // The same text and place as the check used.
    const plan = wouldBe(editor, trayText, projectName)
    insertTextAtCursor(editor, plan.framed, plan.point.pos)
    setBlocks([])
  }, [editor, blocks, key, getAxios, projectId, owner, projectName])

  return { blocks, add, update, remove, move, clear, check, insert }
}
